import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { QuestionType } from '../../../generated/hooks';
import { findScaleMetricForQuestion, questionSummaryKey } from '@arnold/core';
import type { QuestionBinding } from '../definition/types';
import type { NewReport } from '../hooks/useNewReport';
import { useDefinitionMetrics } from '../definition/ReportDefinitionContext';
import { FreetextCard } from './FreetextCard';
import { SelectCard } from './SelectCard';
import { ScaleCard } from './ScaleCard';

/** The same question rendering for overview placements and AI summary sources. */
export function ReportQuestionCard({ report, questionIndex, statementIndex }: QuestionBinding & { report: NewReport }) {
  const { t } = useTranslation('newReport');
  const { url } = useRouteMatch();
  const history = useHistory();
  const { search } = useLocation();
  const root = report.currentGroup;
  const { selectRows } = report;
  const metrics = useDefinitionMetrics();
  const participants = useMemo(() => selectRows(root ? [root] : [], 'participants'), [selectRows, root]);
  const question = report.rawData?.questions.find((item) => item.index === questionIndex);
  const summary = root?.questionSummaries.get(questionSummaryKey(questionIndex, statementIndex));
  const scaleMetric =
    question?.type === QuestionType.Scale ? findScaleMetricForQuestion(metrics, questionIndex) : undefined;
  const metricSummary = scaleMetric ? root?.customMetrics[scaleMetric.id] : undefined;
  if (!question) return <p>{t('definition.unavailable')}</p>;
  const onDetailClick = () => history.push({ pathname: `${url}/detail/${question.index}`, search });
  const props = { question, summary, eligibleCount: root?.responseIds.size ?? 0, onDetailClick };
  if (question.type === QuestionType.Freetext) return <FreetextCard {...props} participants={participants} />;
  if (question.type === QuestionType.Scale) return <ScaleCard {...props} metricSummary={metricSummary} />;
  return <SelectCard {...props} />;
}
